import { Component, input, output } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';

export interface ListViewTab {
  id: string;
  label: string;
  /** Nome do ícone Lucide (kebab-case), ex.: `settings`, exibido à esquerda do label. */
  icon?: string;
  active?: boolean;
  disabled?: boolean;
  badge?: string;
  favorite?: boolean;
  favoriteAriaLabel?: string;
}

@Component({
  selector: 'app-list-view-tabs',
  standalone: true,
  imports: [LucideAngularModule],
  host: {
    class: 'block min-w-0 flex-1',
  },
  template: `
    <div class="w-full border-b border-[color-mix(in_srgb,var(--border)_58%,transparent)]">
      <div class="flex items-center gap-0.5 pl-1 sm:pl-2" role="tablist">
        @for (tab of tabs(); track tab.id) {
          <button
            type="button"
            role="tab"
            [attr.aria-selected]="tab.active"
            [attr.aria-disabled]="tab.disabled"
            [class.opacity-60]="tab.disabled"
            [class.pointer-events-none]="tab.disabled"
            (click)="tab.disabled ? null : selectTab.emit(tab.id)"
            class="flex items-center gap-2 pl-1 pr-3 py-2.5 text-[13px] font-medium border-b-[2px] -mb-px transition-[color,border-color,background-color] duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[color-mix(in_srgb,var(--focus)_45%,transparent)] focus-visible:ring-offset-2 rounded-t-lg"
            [class.border-transparent]="!tab.active"
            [class.text-[var(--muted)]]="!tab.active"
            [class.hover:text-[var(--text)]]="!tab.active && !tab.disabled"
            [class.hover:bg-[color-mix(in_srgb,var(--hover)_55%,transparent)]]="!tab.active && !tab.disabled"
            [class.border-[var(--brand-green)]]="tab.active"
            [class.text-[var(--brand-text)]]="tab.active"
          >
            @if (tab.icon) {
              <lucide-icon [name]="tab.icon" class="h-4 w-4 shrink-0 opacity-[0.88]"></lucide-icon>
            }
            <span class="tracking-[0.01em]">{{ tab.label }}</span>
            @if (tab.badge) {
              <span
                class="text-[10px] px-2 py-0.5 rounded-md bg-[color-mix(in_srgb,var(--hover)_70%,transparent)] text-[var(--muted)] font-semibold tabular-nums"
              >
                {{ tab.badge }}
              </span>
            }
          </button>
          @if (tab.favoriteAriaLabel) {
            <button
              type="button"
              class="h-8 w-8 -ml-1 rounded-lg border border-transparent text-[var(--muted)] hover:text-[var(--text)] hover:bg-[color-mix(in_srgb,var(--hover)_50%,transparent)] hover:border-[color-mix(in_srgb,var(--border)_65%,transparent)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--focus)] transition-colors duration-150"
              [class.text-amber-500]="tab.favorite"
              [attr.aria-label]="tab.favoriteAriaLabel"
              [attr.title]="tab.favoriteAriaLabel"
              (click)="toggleFavorite.emit(tab.id)"
            >
              <lucide-icon [name]="tab.favorite ? 'star' : 'star'" class="h-4 w-4"></lucide-icon>
            </button>
          }
        }
      </div>
    </div>
  `,
})
export class ListViewTabsComponent {
  tabs = input.required<ListViewTab[]>();
  selectTab = output<string>();
  toggleFavorite = output<string>();
}
